import { useState, useEffect } from "react";
import { useHiringRequests } from "../../contexts/HiringRequestsContext"; // Hiring requests context
import { useMasters } from "../../contexts/MastersContext";
import { useDesignation } from "../../contexts/DesignationsContext";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const HiringRequestForm = ({ closeModal, request, isEditing, setNotification }) => {
  const { addHiringRequest, updateHiringRequest } = useHiringRequests(); // Access add and update functions
  const { departments, employmentTypes, workplaces, loading, error } = useMasters(); // Access departments and employment types from Masters context
  const { designations } = useDesignation();
  const [newRequest, setNewRequest] = useState({
    requester_name: "",
    requester_official_email: "",
    employment_type: "",
    department: "",
    designation: "",
    hiring_type: "",
    replaced_employee_name: "",
    number_of_positions: "",
    location: "",
    stipend_ctc_per_month: "",
    minimum_qualification: "",
    years_experience_required: "",
    skill_set: "",
    reporting_to: "",
    tat_days_to_close: "",
    interview_rounds: "",
    interview_process_steps: "",
    other_notes: "",
    additional_suggestions: "",
    rating: "",
    job_description: "",
    kra_kpi: null,
    technical_test_path: null,
  });
  const [errors, setErrors] = useState({}); // Validation errors
  const [loadingState, setLoadingState] = useState(false); // Local loading state
  const [existingFiles, setExistingFiles] = useState({
    job_description_url: "",
    kra_kpi_url: "",
    technical_test_path_url: "",
  });

  // Populate form fields when editing
  useEffect(() => {
    if (isEditing && request) {
      setNewRequest({
        requester_name: request.requester_name || "",
        requester_official_email: request.requester_official_email || "",
        employment_type: request.employment_type || "",
        department: request.department || "",
        designation: request.designation || "",
        hiring_type: request.hiring_type || "",
        replaced_employee_name: request.replaced_employee_name || "",
        number_of_positions: request.number_of_positions || "",
        location: request.location || "",
        stipend_ctc_per_month: request.stipend_ctc_per_month || "",
        minimum_qualification: request.minimum_qualification || "",
        years_experience_required: request.years_experience_required || "",
        skill_set: request.skill_set || "",
        reporting_to: request.reporting_to || "",
        tat_days_to_close: request.tat_days_to_close || "",
        interview_rounds: request.interview_rounds || "",
        interview_process_steps: request.interview_process_steps || "",
        other_notes: request.other_notes || "",
        additional_suggestions: request.additional_suggestions || "",
        rating: request.rating || "",
        job_description: request.job_description || "",
        kra_kpi: null, // Will be handled separately for file display
        technical_test_path: null, // Will be handled separately for file display
      });
      
      // Set existing file URLs for display
      setExistingFiles({
        job_description_url: request.job_description_url || "",
        kra_kpi_url: request.kra_kpi_url || "",
        technical_test_path_url: request.technical_test_path_url || "",
      });
    }
  }, [isEditing, request]);

  // Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewRequest((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle file changes
  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setNewRequest((prev) => ({
      ...prev,
      [name]: files[0] || null,
    }));
  };

  const validate = () => {
    const validationErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Requester Name validation
    if (!newRequest.requester_name.trim()) {
      validationErrors.requester_name = "Requester name is required.";
    } else if (newRequest.requester_name.length > 150) {
      validationErrors.requester_name = "Requester name must be maximum 150 characters.";
    }

    // Requester Email validation
    if (!newRequest.requester_official_email.trim()) {
      validationErrors.requester_official_email = "Official email is required.";
    } else if (!emailRegex.test(newRequest.requester_official_email)) {
      validationErrors.requester_official_email = "Please enter a valid email address.";
    } else if (newRequest.requester_official_email.length > 255) {
      validationErrors.requester_official_email = "Email must be maximum 255 characters.";
    }

    // Employment Type validation
    if (!newRequest.employment_type) {
      validationErrors.employment_type = "Employment type is required.";
    }

    // Department validation
    if (!newRequest.department.trim()) {
      validationErrors.department = "Department is required.";
    } else if (newRequest.department.length > 100) {
      validationErrors.department = "Department must be maximum 100 characters.";
    }

    // Designation validation
    if (!newRequest.designation.trim()) {
      validationErrors.designation = "Designation is required.";
    } else if (newRequest.designation.length > 100) {
      validationErrors.designation = "Designation must be maximum 100 characters.";
    }

    // Hiring Type validation
    if (!newRequest.hiring_type) {
      validationErrors.hiring_type = "Hiring type is required.";
    }

    // Replaced Employee Name validation (required if hiring_type is Replacement)
    if (newRequest.hiring_type === "Replacement" && !newRequest.replaced_employee_name.trim()) {
      validationErrors.replaced_employee_name = "Replaced employee name is required for replacement hiring.";
    } else if (newRequest.replaced_employee_name && newRequest.replaced_employee_name.length > 150) {
      validationErrors.replaced_employee_name = "Replaced employee name must be maximum 150 characters.";
    }

    // Number of Positions validation
    if (!newRequest.number_of_positions || isNaN(newRequest.number_of_positions) || newRequest.number_of_positions < 1) {
      validationErrors.number_of_positions = "Number of positions must be at least 1.";
    }



    // Location validation
    if (!newRequest.location.trim()) {
      validationErrors.location = "Location is required.";
    } else if (newRequest.location.length > 100) {
      validationErrors.location = "Location must be maximum 100 characters.";
    }

    // Stipend/CTC validation
    if (!newRequest.stipend_ctc_per_month || newRequest.stipend_ctc_per_month === "") {
      validationErrors.stipend_ctc_per_month = "Stipend/CTC per month is required.";
    } else if (isNaN(newRequest.stipend_ctc_per_month) || newRequest.stipend_ctc_per_month < 0) {
      validationErrors.stipend_ctc_per_month = "Stipend/CTC must be a positive number.";
    }

    // Minimum Qualification validation
    if (!newRequest.minimum_qualification.trim()) {
      validationErrors.minimum_qualification = "Minimum qualification is required.";
    } else if (newRequest.minimum_qualification.length > 100) {
      validationErrors.minimum_qualification = "Minimum qualification must be maximum 100 characters.";
    }

    // Years Experience validation
    if (!newRequest.years_experience_required || newRequest.years_experience_required === "") {
      validationErrors.years_experience_required = "Years experience is required.";
    } else if (isNaN(newRequest.years_experience_required) || newRequest.years_experience_required < 0 || newRequest.years_experience_required > 50) {
      validationErrors.years_experience_required = "Years experience must be a number between 0 and 50.";
    }

    // Skill Set validation
    if (!newRequest.skill_set.trim()) {
      validationErrors.skill_set = "Skill set is required.";
    }

    // Reporting To validation
    if (!newRequest.reporting_to.trim()) {
      validationErrors.reporting_to = "Reporting to is required.";
    } else if (newRequest.reporting_to.length > 150) {
      validationErrors.reporting_to = "Reporting to must be maximum 150 characters.";
    }

    // TAT Days validation
    if (!newRequest.tat_days_to_close || newRequest.tat_days_to_close === "") {
      validationErrors.tat_days_to_close = "TAT days to close is required.";
    } else if (isNaN(newRequest.tat_days_to_close) || newRequest.tat_days_to_close < 0) {
      validationErrors.tat_days_to_close = "TAT days must be a positive number.";
    }

    // Interview Rounds validation
    if (!newRequest.interview_rounds) {
      validationErrors.interview_rounds = "Interview rounds is required.";
    }

    // Rating validation
    if (!newRequest.rating || newRequest.rating === "") {
      validationErrors.rating = "Rating is required.";
    } else if (isNaN(newRequest.rating) || newRequest.rating < 1 || newRequest.rating > 5) {
      validationErrors.rating = "Rating must be between 1 and 5.";
    }

    // Interview Process Steps validation
    if (!newRequest.interview_process_steps.trim()) {
      validationErrors.interview_process_steps = "Interview process steps is required.";
    }

    // Job Description validation (required)
    if (!newRequest.job_description.trim()) {
      validationErrors.job_description = "Job description is required.";
    }

    if (newRequest.kra_kpi) {
      const file = newRequest.kra_kpi;
      const maxSize = 1024 * 1024; // 1 MB
      
      if (file.size > maxSize) {
        validationErrors.kra_kpi = "File size must be less than 1 MB.";
      } else if (file.type !== 'application/pdf') {
        validationErrors.kra_kpi = "File must be PDF.";
      }
    }

    if (newRequest.technical_test_path) {
      const file = newRequest.technical_test_path;
      const maxSize = 1024 * 1024; // 1 MB
      
      if (file.size > maxSize) {
        validationErrors.technical_test_path = "File size must be less than 1 MB.";
      } else if (file.type !== 'application/pdf') {
        validationErrors.technical_test_path = "File must be PDF.";
      }
    }

    return validationErrors;
  };

  // new code
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Form submission started");
    
    const validationErrors = validate();
    console.log("Validation errors:", validationErrors);
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      console.log("Form has validation errors, stopping submission");
      return;
    }
    
    console.log("Form data to submit:", newRequest);
    
    // Log FormData contents for debugging
    const formData = new FormData();
    Object.keys(newRequest).forEach(key => {
      if (newRequest[key] !== null && typeof newRequest[key] !== 'object') {
        // Skip replaced_employee_name if hiring_type is not "Replacement"
        if (key === 'replaced_employee_name' && newRequest.hiring_type !== 'Replacement') {
          return;
        }
        // Skip empty replaced_employee_name even if hiring_type is "Replacement"
        if (key === 'replaced_employee_name' && (!newRequest[key] || newRequest[key].trim() === '')) {
          return;
        }
        formData.append(key, newRequest[key]);
      }
    });
    if (newRequest.job_description) {
      formData.append('job_description', newRequest.job_description);
    }
    if (newRequest.kra_kpi) {
      formData.append('kra_kpi', newRequest.kra_kpi);
    }
    if (newRequest.technical_test_path) {
      formData.append('technical_test_path', newRequest.technical_test_path);
    }
    formData.append('status', 'Pending');
    
    console.log("FormData entries:");
    for (let [key, value] of formData.entries()) {
      console.log(`${key}:`, value);
    }
  
    setLoadingState(true); // Start loading
    try {
      
      if (isEditing && request) {
        formData.append('id', request.id);
        // formData.append('status', request.status);
        console.log("Updating hiring request with FormData");
        const result = await updateHiringRequest(formData);
        console.log("Update result:", result);
        if (!result.success) {
          setNotification({
            message: result.error,
            type: "error"
          });
          return;
        } else {
          setNotification({
            message: "Hiring request updated successfully",
            type: "success"
          });
        }
      } else {
        console.log("Adding new hiring request with FormData");
        const result = await addHiringRequest(formData);
        console.log("Add result:", result);
        if (!result.success) {
          setNotification({
            message: result.error,
            type: "error"
          });
          return;
        } else {
          setNotification({
            message: "Hiring request added successfully",
            type: "success"
          });
        }
      }
  
      setNewRequest({
        requester_name: "",
        requester_official_email: "",
        employment_type: "",
        department: "",
        designation: "",
        hiring_type: "",
        replaced_employee_name: "",
        number_of_positions: "",
        location: "",
        stipend_ctc_per_month: "",
        minimum_qualification: "",
        years_experience_required: "",
        skill_set: "",
        reporting_to: "",
        tat_days_to_close: "",
        interview_rounds: "",
        interview_process_steps: "",
        other_notes: "",
        additional_suggestions: "",
        rating: "",
        job_description: "",
        kra_kpi: null,
        technical_test_path: null,
      });
  
      setErrors({});
      closeModal(); // Close modal after successful submission
    } catch (error) {
      console.error("Error submitting hiring request:", error);
      setNotification({
        message: `Error submitting form: ${error.message}`,
        type: "error"
      });
    } finally {
      setLoadingState(false); // Stop loading
    }
  };

  return (
    <div className="w-full">

      {/* Contextual Info Banner */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-4 rounded-lg mb-6 border border-blue-200 dark:border-blue-800">
        <p className="text-blue-800 dark:text-blue-200 text-sm flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          {isEditing ? "Modify the details of your hiring request below" : "Fill in all required details to create a new hiring request"}
        </p>
      </div>

      {/* Form Content */}
      <form className="space-y-6" onSubmit={handleSubmit} noValidate>
        
        {/* Requester Information Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900 rounded-md flex items-center justify-center">
              <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
              </svg>
            </div>
            <h3 className="text-base font-semibold text-gray-800 dark:text-white">Requester Information</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Requester Name */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
                Requester Name
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                name="requester_name"
                value={newRequest.requester_name}
                onChange={handleChange}
                placeholder="Enter requester name"
                maxLength="150"
                className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                  errors.requester_name 
                    ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600" 
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
                } dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
                required
              />
              {errors.requester_name && (
                <p className="text-red-500 text-sm flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                  </svg>
                  {errors.requester_name}
                </p>
              )}
            </div>

            {/* Requester Email */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                </svg>
                Official Email
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="email"
                name="requester_official_email"
                value={newRequest.requester_official_email}
                onChange={handleChange}
                placeholder="requester@company.com"
                maxLength="255"
                className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                  errors.requester_official_email 
                    ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600" 
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
                } dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
                required
              />
              {errors.requester_official_email && (
                <p className="text-red-500 text-sm flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                  </svg>
                  {errors.requester_official_email}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Position & Employment Details */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 bg-purple-100 dark:bg-purple-900 rounded-md flex items-center justify-center">
              <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M8 6a2 2 0 00-2 2v6.002"></path>
              </svg>
            </div>
            <h3 className="text-base font-semibold text-gray-800 dark:text-white">Position & Employment Details</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Employment Type */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                Employment Type
                <span className="text-red-500 ml-1">*</span>
              </label>
              {loading ? (
                <div className="flex items-center gap-2 p-4 text-gray-500">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                  </svg>
                  Loading employment types...
                </div>
              ) : error ? (
                <p className="text-red-500 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">{error}</p>
              ) : (
                <select
                  name="employment_type"
                  value={newRequest.employment_type}
                  onChange={handleChange}
                  className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                    errors.employment_type 
                      ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600" 
                      : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
                  } dark:bg-gray-800 dark:text-white`}
                  required
                >
                  <option value="">Select Employment Type</option>
                  {employmentTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              )}
              {errors.employment_type && (
                <p className="text-red-500 text-sm flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                  </svg>
                  {errors.employment_type}
                </p>
              )}
            </div>

            {/* Department */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
                </svg>
                Department
                <span className="text-red-500 ml-1">*</span>
              </label>
              <select
                name="department"
                value={newRequest.department}
                onChange={handleChange}
                className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                  errors.department 
                    ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600" 
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
                } dark:bg-gray-800 dark:text-white`}
                required
              >
                <option value="">Select Department</option>
                {departments && departments.length > 0 ? (
                  departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>Loading departments...</option>
                )}
              </select>
              {errors.department && (
                <p className="text-red-500 text-sm flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                  </svg>
                  {errors.department}
                </p>
              )}
            </div>

            {/* Designation */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M8 6a2 2 0 00-2 2v6.002"></path>
                </svg>
                Designation
                <span className="text-red-500 ml-1">*</span>
              </label>
              <select
                name="designation"
                value={newRequest.designation}
                onChange={handleChange}
                className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                  errors.designation 
                    ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600" 
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
                } dark:bg-gray-800 dark:text-white`}
                required
              >
                <option value="">Select Designation</option>
                {designations && designations.length > 0 ? (
                  designations.map((designation) => (
                    <option key={designation.id} value={designation.id}>
                      {designation.name}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>Loading designations...</option>
                )}
              </select>
              {errors.designation && (
                <p className="text-red-500 text-sm flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                  </svg>
                  {errors.designation}
                </p>
              )}
            </div>

            {/* Hiring Type */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                Hiring Type
                <span className="text-red-500 ml-1">*</span>
              </label>
              <select
                name="hiring_type"
                value={newRequest.hiring_type}
                onChange={handleChange}
                className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                  errors.hiring_type 
                    ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600" 
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
                } dark:bg-gray-800 dark:text-white`}
                required
              >
                <option value="">Select Hiring Type</option>
                <option value="New">New</option>
                <option value="Replacement">Replacement</option>
              </select>
              {errors.hiring_type && (
                <p className="text-red-500 text-sm flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                  </svg>
                  {errors.hiring_type}
                </p>
              )}
            </div>

            {/* Replaced Employee Name - Conditional */}
            {newRequest.hiring_type === "Replacement" && (
              <div className="space-y-2">
                <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                  <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                  </svg>
                  Replaced Employee Name
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  name="replaced_employee_name"
                  value={newRequest.replaced_employee_name}
                  onChange={handleChange}
                  placeholder="Enter replaced employee name"
                  maxLength="150"
                  className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                    errors.replaced_employee_name 
                      ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600" 
                      : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
                  } dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
                  required={newRequest.hiring_type === "Replacement"}
                />
                {errors.replaced_employee_name && (
                  <p className="text-red-500 text-sm flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                    </svg>
                    {errors.replaced_employee_name}
                  </p>
                )}
              </div>
            )}

            {/* Number of Positions */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"></path>
                </svg>
                Number of Positions
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="number"
                name="number_of_positions"
                value={newRequest.number_of_positions}
                onChange={handleChange}
                placeholder="1"
                min="1"
                className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                  errors.number_of_positions 
                    ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600" 
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
                } dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
                required
              />
              {errors.number_of_positions && (
                <p className="text-red-500 text-sm flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                  </svg>
                  {errors.number_of_positions}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Location & Compensation Details */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 bg-green-100 dark:bg-green-900 rounded-md flex items-center justify-center">
              <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
              </svg>
            </div>
            <h3 className="text-base font-semibold text-gray-800 dark:text-white">Location & Compensation Details</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Location */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
                Location
                <span className="text-red-500 ml-1">*</span>
              </label>
              <select
                name="location"
                value={newRequest.location}
                onChange={handleChange}
                className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                  errors.location 
                    ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600" 
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
                } dark:bg-gray-800 dark:text-white`}
                required
              >
                <option value="">Select Location</option>
                {workplaces && workplaces.length > 0 ? (
                  workplaces.map((workplace) => (
                    <option key={workplace.id} value={workplace.id}>
                      {workplace.name}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>Loading locations...</option>
                )}
              </select>
              {errors.location && (
                <p className="text-red-500 text-sm flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                  </svg>
                  {errors.location}
                </p>
              )}
            </div>

            {/* Stipend/CTC per Month */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"></path>
                </svg>
                Stipend/CTC per Month
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="number"
                name="stipend_ctc_per_month"
                value={newRequest.stipend_ctc_per_month}
                onChange={handleChange}
                placeholder="0"
                min="0"
                step="0.01"
                className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                  errors.stipend_ctc_per_month 
                    ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600" 
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
                } dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
                required
              />
              {errors.stipend_ctc_per_month && (
                <p className="text-red-500 text-sm flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                  </svg>
                  {errors.stipend_ctc_per_month}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Requirements & Skills */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 bg-orange-100 dark:bg-orange-900 rounded-md flex items-center justify-center">
              <svg className="w-4 h-4 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
              </svg>
            </div>
            <h3 className="text-base font-semibold text-gray-800 dark:text-white">Requirements & Skills</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Minimum Qualification */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l9-5-9-5-9 5 9 5z"></path>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"></path>
                </svg>
                Minimum Qualification
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                name="minimum_qualification"
                value={newRequest.minimum_qualification}
                onChange={handleChange}
                placeholder="e.g., Bachelor's Degree, Master's Degree"
                maxLength="100"
                className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                  errors.minimum_qualification 
                    ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600" 
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
                } dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
                required
              />
              {errors.minimum_qualification && (
                <p className="text-red-500 text-sm flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                  </svg>
                  {errors.minimum_qualification}
                </p>
              )}
            </div>

            {/* Years Experience Required */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                Years Experience Required
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="number"
                name="years_experience_required"
                value={newRequest.years_experience_required}
                onChange={handleChange}
                placeholder="e.g., 2, 5, 10"
                min="0"
                max="50"
                className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                  errors.years_experience_required 
                    ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600" 
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
                } dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
                required
              />
              {errors.years_experience_required && (
                <p className="text-red-500 text-sm flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                  </svg>
                  {errors.years_experience_required}
                </p>
              )}
            </div>
          </div>

          {/* Skill Set */}
          <div className="space-y-2">
            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
              <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
              </svg>
              Skill Set
              <span className="text-red-500 ml-1">*</span>
            </label>
            <textarea
              name="skill_set"
              value={newRequest.skill_set}
              onChange={handleChange}
              placeholder="e.g., React, Node.js, MongoDB, TypeScript, AWS"
              rows="3"
              className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                errors.skill_set 
                  ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600" 
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
              } dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
              required
            />
            {errors.skill_set && (
              <p className="text-red-500 text-sm flex items-center gap-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                </svg>
                {errors.skill_set}
              </p>
            )}
          </div>
        </div>

        {/* Interview & Process Details */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 bg-indigo-100 dark:bg-indigo-900 rounded-md flex items-center justify-center">
              <svg className="w-4 h-4 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
              </svg>
            </div>
            <h3 className="text-base font-semibold text-gray-800 dark:text-white">Interview & Process Details</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Reporting To */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
                Reporting To
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                name="reporting_to"
                value={newRequest.reporting_to}
                onChange={handleChange}
                placeholder="e.g., John Doe, Manager"
                maxLength="150"
                className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                  errors.reporting_to 
                    ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600" 
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
                } dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
                required
              />
              {errors.reporting_to && (
                <p className="text-red-500 text-sm flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                  </svg>
                  {errors.reporting_to}
                </p>
              )}
            </div>

            {/* TAT Days to Close */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                TAT Days to Close
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="number"
                name="tat_days_to_close"
                value={newRequest.tat_days_to_close}
                onChange={handleChange}
                placeholder="0"
                min="0"
                className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                  errors.tat_days_to_close 
                    ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600" 
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
                } dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
                required
              />
              {errors.tat_days_to_close && (
                <p className="text-red-500 text-sm flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                  </svg>
                  {errors.tat_days_to_close}
                </p>
              )}
            </div>

            {/* Interview Rounds */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                </svg>
                Interview Rounds
                <span className="text-red-500 ml-1">*</span>
              </label>
              <select
                name="interview_rounds"
                value={newRequest.interview_rounds}
                onChange={handleChange}
                className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                  errors.interview_rounds 
                    ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600" 
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
                } dark:bg-gray-800 dark:text-white`}
                required
              >
                <option value="">Select Interview Rounds</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5</option>
              </select>
              {errors.interview_rounds && (
                <p className="text-red-500 text-sm flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                  </svg>
                  {errors.interview_rounds}
                </p>
              )}
            </div>
          </div>

          {/* Job Description */}
          <div className="space-y-2">
            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
              <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              Job Description
              <span className="text-red-500 ml-1">*</span>
            </label>
            
            <div className="relative">
              <ReactQuill
                theme="snow"
                value={newRequest.job_description}
                onChange={(content) =>
                  setNewRequest((prev) => ({ ...prev, job_description: content }))
                }
                className={`bg-white dark:bg-gray-800 rounded-xl overflow-hidden border-2 transition-all duration-200 ${
                  errors.job_description ? "border-red-400" : "border-gray-200 dark:border-gray-600"
                }`}
                style={{
                  minHeight: '120px'
                }}
                modules={{
                  toolbar: [
                    [{ 'header': [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                    [{ 'color': [] }, { 'background': [] }],
                    [{ 'align': [] }],
                    ['link', 'image'],
                    ['clean']
                  ]
                }}
                formats={[
                  'header',
                  'bold', 'italic', 'underline', 'strike',
                  'list', 'bullet',
                  'color', 'background',
                  'align',
                  'link', 'image'
                ]}
              />
              {errors.job_description && (
                <div className="flex items-center gap-1 mt-2 text-red-500 text-sm">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                  </svg>
                  {errors.job_description}
                </div>
              )}
            </div>
          </div>

          {/* Interview Process Steps */}
          <div className="space-y-2">
            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
              <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
              </svg>
                              Interview Process Steps
                <span className="text-red-500 ml-1">*</span>
            </label>
            <textarea
              name="interview_process_steps"
              value={newRequest.interview_process_steps}
              onChange={handleChange}
              placeholder="Describe the interview process steps..."
              rows="3"
                              className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                  errors.interview_process_steps 
                    ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600" 
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
              } dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
              required
            />
            {errors.interview_process_steps && (
              <p className="text-red-500 text-sm flex items-center gap-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                </svg>
                {errors.interview_process_steps}
              </p>
            )}
          </div>
        </div>

        {/* Additional Notes & Suggestions */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 bg-yellow-100 dark:bg-yellow-900 rounded-md flex items-center justify-center">
              <svg className="w-4 h-4 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
              </svg>
            </div>
            <h3 className="text-base font-semibold text-gray-800 dark:text-white">Additional Notes & Suggestions</h3>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {/* Other Notes */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                </svg>
                Other Notes
              </label>
              <textarea
                name="other_notes"
                value={newRequest.other_notes}
                onChange={handleChange}
                placeholder="Any additional notes or comments..."
                rows="3"
                className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                  errors.other_notes 
                    ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600" 
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
                } dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
              />
              {errors.other_notes && (
                <p className="text-red-500 text-sm flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                  </svg>
                  {errors.other_notes}
                </p>
              )}
            </div>

            {/* Additional Suggestions */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
                </svg>
                Additional Suggestions
              </label>
              <textarea
                name="additional_suggestions"
                value={newRequest.additional_suggestions}
                onChange={handleChange}
                placeholder="Any suggestions for the hiring process..."
                rows="3"
                className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                  errors.additional_suggestions 
                    ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600" 
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
                } dark:bg-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500`}
              />
              {errors.additional_suggestions && (
                <p className="text-red-500 text-sm flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                  </svg>
                  {errors.additional_suggestions}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* File Uploads */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 bg-red-100 dark:bg-red-900 rounded-md flex items-center justify-center">
              <svg className="w-4 h-4 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
              </svg>
            </div>
            <h3 className="text-base font-semibold text-gray-800 dark:text-white">File Uploads</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* KRA/KPI File */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                </svg>
                KRA/KPI File
                <span className="text-xs text-gray-500 ml-1">(PDF - Max 1 MB)</span>
              </label>
              
              {/* Show existing file if available */}
              {isEditing && existingFiles.kra_kpi_url && (
                <div className="mb-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <p className="text-sm text-green-700 dark:text-green-300 mb-2">Current file:</p>
                  <a 
                    href={existingFiles.kra_kpi_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                    </svg>
                    View Current File
                  </a>
                </div>
              )}
              
              <input
                type="file"
                name="kra_kpi"
                onChange={handleFileChange}
                accept=".pdf"
                className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                  errors.kra_kpi 
                    ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600" 
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
                } dark:bg-gray-800 dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/20 dark:file:text-blue-400`}
              />
              {errors.kra_kpi && (
                <p className="text-red-500 text-sm flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                  </svg>
                  {errors.kra_kpi}
                </p>
              )}
            </div>

            {/* Technical Test Path */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                Technical Test Path
                <span className="text-xs text-gray-500 ml-1">(PDF - Max 1 MB)</span>
              </label>
              
              {/* Show existing file if available */}
              {isEditing && existingFiles.technical_test_path_url && (
                <div className="mb-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <p className="text-sm text-green-700 dark:text-green-300 mb-2">Current file:</p>
                  <a 
                    href={existingFiles.technical_test_path_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                    </svg>
                    View Current File
                  </a>
                </div>
              )}
              
              <input
                type="file"
                name="technical_test_path"
                onChange={handleFileChange}
                accept=".pdf"
                className={`w-full px-3 py-2.5 border-2 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                  errors.technical_test_path 
                    ? "border-red-300 bg-red-50 dark:bg-red-900/10 dark:border-red-600" 
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500"
                } dark:bg-gray-800 dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900/20 dark:file:text-blue-400`}
              />
              {errors.technical_test_path && (
                <p className="text-red-500 text-sm flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                  </svg>
                  {errors.technical_test_path}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Rating */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 bg-pink-100 dark:bg-pink-900 rounded-md flex items-center justify-center">
              <svg className="w-4 h-4 text-pink-600 dark:text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
              </svg>
            </div>
            <h3 className="text-base font-semibold text-gray-800 dark:text-white">Rating</h3>
          </div>

          <div className="space-y-2">
            <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
              <svg className="w-4 h-4 mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
              </svg>
              Rating (1-5)
              <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="flex items-center space-x-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => {
                    setNewRequest(prev => ({
                      ...prev,
                      rating: star.toString()
                    }));
                  }}
                  className={`transition-all duration-200 transform hover:scale-110 ${
                    parseInt(newRequest.rating) >= star
                      ? "text-red-500 hover:text-red-600"
                      : "text-gray-300 hover:text-gray-400"
                  }`}
                >
                  <svg 
                    className="w-8 h-8" 
                    fill={parseInt(newRequest.rating) >= star ? "currentColor" : "none"}
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth="2" 
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                    />
                  </svg>
                </button>
              ))}
            </div>
            {newRequest.rating && (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Selected: {newRequest.rating} heart{parseInt(newRequest.rating) === 1 ? '' : 's'}
              </p>
            )}
            {errors.rating && (
              <p className="text-red-500 text-sm flex items-center gap-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                </svg>
                {errors.rating}
              </p>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="submit"
            disabled={loadingState}
            // onClick={() => console.log("Submit button clicked")}
            className={`w-full flex justify-center items-center gap-2 px-6 py-3 rounded-lg font-semibold text-white transition-all duration-200 transform ${
              loadingState
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-500 hover:scale-[1.01] active:scale-[0.99] shadow-md hover:shadow-lg"
            } focus:outline-none focus:ring-4 focus:ring-blue-500/25`}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isEditing ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              )}
            </svg>
            <span>
              {isEditing ? "Update Request" : "Create Request"}
            </span>
            {loadingState && (
              <svg
                className="animate-spin h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
            )}
          </button>
        </div>

        {/* Footer Info */}
        <div className="text-center text-xs text-gray-500 dark:text-gray-400 pt-3 border-t border-gray-100 dark:border-gray-800">
          <p className="flex items-center justify-center gap-1">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            All fields marked with <span className="text-red-500">*</span> are required
          </p>
        </div>
      </form>


    </div> 
  );
};

export default HiringRequestForm;